import type { OrderDetail } from '@insolech/sdk';
import type {
  PrescriptionFormState,
  PrescriptionModification,
} from './wizard/PrescriptionForm';
import type { SolutionSubtype } from './wizard/SolutionSubtypePicker';

/**
 * Shared "persisted order_line -> editable PrescriptionFormState" hydration.
 *
 * This is the single source of truth for turning a stored
 * `order_lines.configuration` (JSONB) back into wizard/dialog form state. It is
 * consumed by BOTH the Edit-prescription dialog (which edits a line in place)
 * AND the wizard's `/orders/new?draft=<id>` resume mode (which reopens a draft
 * in the full new-order flow). Keeping one copy avoids the two paths drifting.
 */

type Line = OrderDetail['lines'][number];

const KNOWN_SUBTYPES: ReadonlyArray<SolutionSubtype> = [
  'pa12_insole',
  'tpu_insole',
  'polypropylene_insole',
  'carbon_insole',
  'eva_insole',
  'fibreglass_insole',
  'orthopaedic_sandal',
  'insoles_refurbishment',
];

export function isSolutionSubtype(
  v: string | null | undefined,
): v is SolutionSubtype {
  return !!v && (KNOWN_SUBTYPES as readonly string[]).includes(v);
}

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

export function extractModifications(line: Line): PrescriptionModification[] {
  const raw = (line.configuration as Record<string, unknown>)?.modifications;
  const fromLine = line.modifications as unknown;
  const candidate = Array.isArray(fromLine) && fromLine.length > 0 ? fromLine : raw;
  if (!Array.isArray(candidate)) return [];
  const out: PrescriptionModification[] = [];
  for (const m of candidate) {
    const item = m as Record<string, unknown>;
    const region = item.region as string | undefined;
    const modification_type = item.modification_type as string | undefined;
    if (!region || !modification_type) continue;
    const intensity = item.intensity as PrescriptionModification['intensity'];
    const next: PrescriptionModification = {
      region: region as PrescriptionModification['region'],
      modification_type,
    };
    if (intensity) next.intensity = intensity;
    if (item.parameters && typeof item.parameters === 'object') {
      next.parameters = item.parameters as Record<string, unknown>;
    }
    out.push(next);
  }
  return out;
}

/** Pull a PrescriptionFormState out of a persisted order_line. */
export function hydratePrescriptionFormState(line: Line): PrescriptionFormState {
  const cfg = (line.configuration ?? {}) as Record<string, unknown>;
  const sizeNode = cfg.size as
    | {
        left?: { length_mm?: number; width_mm?: number };
        right?: { length_mm?: number; width_mm?: number };
      }
    | undefined;
  const next: PrescriptionFormState = {
    line_type: (line.line_type as PrescriptionFormState['line_type']) ?? 'custom_insole',
    modifications: extractModifications(line),
  };
  if (line.product?.id) next.product_id = line.product.id;
  if (typeof cfg.material === 'string') next.material = cfg.material;
  if (typeof cfg.thickness_mm === 'number') next.thickness_mm = cfg.thickness_mm;
  if (typeof cfg.cover === 'string') next.cover = cfg.cover;
  if (typeof cfg.pathology === 'string') next.pathology = cfg.pathology;
  if (typeof cfg.observations === 'string') next.observations = cfg.observations;
  if (sizeNode?.left?.length_mm) next.size_left_length_mm = sizeNode.left.length_mm;
  if (sizeNode?.right?.length_mm) next.size_right_length_mm = sizeNode.right.length_mm;
  if (sizeNode?.left?.width_mm) next.size_left_width_mm = sizeNode.left.width_mm;
  if (sizeNode?.right?.width_mm) next.size_right_width_mm = sizeNode.right.width_mm;
  // R28 deep prescription state - structuredClone so callers can mutate freely
  // without touching the cached configuration object.
  const deep = cfg.deep as Record<string, unknown> | undefined;
  if (deep) {
    next.deep = deepClone(deep) as NonNullable<PrescriptionFormState['deep']>;
  }
  return next;
}
